import { Link, useParams } from 'react-router-dom';
import { actors } from '@/mocks/actors';
import { episodes } from '@/mocks/episodes';
import { newsList } from '@/mocks/news';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import NewsCard from '@/components/feature/NewsCard';
import usePageMeta from '@/hooks/usePageMeta';

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();
  const actor = actors.find((item) => String(item.id) === id);

  usePageMeta({
    title: actor
      ? `${actor.name}（${actor.role}）演员资料 - 女友的妈妈`
      : '演员资料 - 女友的妈妈',
    description: actor
      ? `《女友的妈妈》演员${actor.name}资料介绍，${actor.role}，出生地${actor.birthPlace}，了解其个人简介及相关剧集与资讯。`
      : '《女友的妈妈》演员详情页',
    keywords: '女友的妈妈演员资料,女友的妈妈演员简介,主演介绍',
  });

  if (!actor) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <div className="max-w-[800px] mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员介绍', to: '/actors' }, { label: '演员资料' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-user-unfollow-line"
              title="该演员信息暂未发布"
              description="你访问的演员资料可能已下线或链接有误，返回演员列表看看其他内容吧。"
            />
            <div className="mt-6 text-center">
              <Link to="/actors" className="text-sm text-primary-600 hover:text-accent-600 transition-colors">
                返回演员列表
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const actorEpisodes = episodes.filter((episode) => actor.episodeIds.includes(episode.id));
  const relatedNews = newsList.filter((item) =>
    item.episodeIds.some((episodeId) => actor.episodeIds.includes(episodeId))
  );

  const profileRows = [
    { label: '饰演角色', value: actor.role.replace('饰 ', '') },
    { label: '出生年月', value: actor.birth },
    { label: '出生地', value: actor.birthPlace },
    { label: '身高', value: actor.height },
  ];

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-[1080px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '演员介绍', to: '/actors' },
              { label: actor.name },
            ]}
          />
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-[1080px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={actor.avatar}
                  alt={`演员 ${actor.name} 饰演 ${actor.role} 资料大图`}
                  title={`${actor.name} 女友的妈妈 演员资料与角色介绍`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                <i className="ri-user-star-line"></i>
                {actor.role}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3">
                {actor.name}
              </h1>

              <dl className="grid grid-cols-2 gap-3 md:gap-4 mt-5">
                {profileRows.map((row) => (
                  <div key={row.label} className="rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <dt className="text-xs text-foreground-500">{row.label}</dt>
                    <dd className="text-sm md:text-base font-medium text-foreground-900 mt-1 line-clamp-1">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <section className="mt-8">
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-3">
                  <i className="ri-profile-line text-primary-600"></i>
                  个人简介
                </h2>
                <p className="text-sm md:text-base leading-loose text-foreground-700">{actor.bio}</p>
              </section>

              <div className="mt-6">
                <Link
                  to="/actors"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-accent-600 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回演员列表
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
              <i className="ri-movie-line text-primary-600"></i>
              饰演角色相关剧集
            </h2>
            <div className="flex flex-wrap gap-2.5">
              {actorEpisodes.map((episode) => (
                <Link
                  key={episode.id}
                  to={`/episode/${episode.id}`}
                  className="px-4 py-2 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  第{String(episode.number).padStart(2, '0')}集 · {episode.title}
                </Link>
              ))}
            </div>
          </section>

          {relatedNews.length > 0 && (
            <section className="mt-12">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
                <i className="ri-newspaper-line text-primary-600"></i>
                相关资讯
              </h2>
              <div className="space-y-3 md:space-y-4">
                {relatedNews.slice(0, 3).map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}